/**
 * Service Worker Manager
 *
 * Provides utilities for managing service workers
 * and handling caching-related issues.
 */

/**
 * Unregisters all service workers registered for the current origin
 * @returns Promise that resolves when all service workers are unregistered
 */
export const unregisterAllServiceWorkers = async (): Promise<boolean> => {
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    if (registrations.length === 0) {
      return true;
    }

    const results = await Promise.all(
      registrations.map((registration) =>
        registration
          .unregister()
          .then((success) => {
            return success;
          })
          .catch((error) => {
            console.error("Error unregistering service worker:", error);
            return false;
          })
      )
    );

    // Check if all unregistrations were successful
    const allSuccessful = results.every((result) => result === true);
    return allSuccessful;
  } catch (error) {
    console.error("Error unregistering service workers:", error);
    return false;
  }
};

/**
 * Clears all caches for the current origin
 * @returns Promise that resolves when all caches are cleared
 */
export const clearAllCaches = async (): Promise<boolean> => {
  if (!("caches" in window)) {
    return false;
  }

  try {
    const cacheNames = await caches.keys();

    if (cacheNames.length === 0) {
      return true;
    }

    await Promise.all(
      cacheNames.map((cacheName) =>
        caches
          .delete(cacheName)
          .then((success) => {
            return success;
          })
          .catch((error) => {
            console.error(`Error clearing cache '${cacheName}':`, error);
            return false;
          })
      )
    );

    return true;
  } catch (error) {
    console.error("Error clearing caches:", error);
    return false;
  }
};

/**
 * Marks service workers as disabled in localStorage to prevent
 * future registrations or to trigger fallback behavior
 */
export const markServiceWorkersDisabled = (): void => {
  localStorage.setItem("pwa-disable-sw", "true");
  localStorage.setItem("firestore-error", "true");
  localStorage.setItem("sw-disabled-timestamp", Date.now().toString());
};

/**
 * Removes service worker disabled flags
 */
export const resetServiceWorkerFlags = (): void => {
  localStorage.removeItem("pwa-disable-sw");
  localStorage.removeItem("firestore-error");
  localStorage.removeItem("sw-disabled-timestamp");
};

/**
 * Checks if service workers are currently disabled
 */
export const areServiceWorkersDisabled = (): boolean => {
  return localStorage.getItem("pwa-disable-sw") === "true";
};

/**
 * Fully resets service workers and caches, then reloads the page
 * @param showAlert Whether to show an alert before reloading
 */
export const resetBrowserState = async (
  showAlert: boolean = true
): Promise<void> => {
  markServiceWorkersDisabled();

  // Run unregistration and cache clearing in parallel
  await Promise.all([unregisterAllServiceWorkers(), clearAllCaches()]);

  // Add a URL parameter to indicate the service worker was cleared
  const url = new URL(window.location.href);
  url.searchParams.set("sw-cleared", "true");

  if (showAlert) {
    alert(
      "Browser caches and service workers have been cleared. The page will now reload."
    );
  }

  // Navigate to the updated URL
  window.location.href = url.toString();
};

/**
 * Detects if the current app is experiencing service worker related issues
 */
export const detectServiceWorkerIssues = (): boolean => {
  // Check localStorage flags
  const swDisabled = localStorage.getItem("pwa-disable-sw") === "true";
  const firestoreError = localStorage.getItem("firestore-error") === "true";

  // Check URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const forceSafeMode = urlParams.get("safe-mode") === "true";
  const forceDisableSW = urlParams.get("disable-sw") === "true";

  return swDisabled || firestoreError || forceSafeMode || forceDisableSW;
};

/**
 * Checks if there was a recent Firestore channel error
 * and if a refresh might help
 */
export const checkForFirestoreChannelIssue = (): boolean => {
  const hasFirestoreError = localStorage.getItem("firestore-error") === "true";
  if (!hasFirestoreError) return false;

  // Check if the error was detected recently (less than 5 minutes ago)
  const timestamp = localStorage.getItem("sw-disabled-timestamp");
  if (!timestamp) return false;

  const errorTime = parseInt(timestamp, 10);
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

  // If error was detected in the last 5 minutes, suggest reload
  return errorTime > fiveMinutesAgo;
};

/**
 * Safely handles pages that might be impacted by service worker issues
 * by ensuring the appropriate flags are set
 */
export const safeGuardPage = (): void => {
  const urlParams = new URLSearchParams(window.location.search);

  // If there's a URL parameter indicating issues, mark service workers as disabled
  if (
    urlParams.get("safe-mode") === "true" ||
    urlParams.get("disable-sw") === "true"
  ) {
    markServiceWorkersDisabled();
  }
};
